import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import ModalPortal from "../Modal/ModalPortal"
import Animate, { AnimationTypes } from "../Animate/Animate"
import Button, { ButtonIcon } from "../Button/Button"
import InputField from "../InputField/InputField"
import { GenericDropDown } from "../Dropdown/Dropdown"
import { QuickEditBooleanSelect, QuickEditGrid, type QuickEditColumn } from "./QuickEditGrid"
import { EEnableStatusType } from "../../Enum/EEnableStatusType"
import type { ProductEntity } from "../../Objects/ProductsAndCatalogItems/ProductEntity"
import { categoryMemory } from "../../Objects/Memory/Memory"
import { useQuickEditProducts, type ProductQuickEditItem } from "../../hooks/Products/useQuickEditProducts"
import { TitlePageResponsiveIconSmall } from "../Titles/Titles"
import { PopupHelpButton } from "../Help/PopupHelpButton"
import { faCheck, faChevronLeft, faChevronRight, faClose, faTableCells } from "@fortawesome/free-solid-svg-icons"
import THEME from "../../configuration/Theme"
import { SIZE } from "../../configuration/Sizes"
import { INPUT_SIZE } from "../../configuration/InputSize"

interface ProductQuickEditPopupProps {
  products: ProductEntity[]
  onClose: () => void
  onReload: () => Promise<void>
}

type PriceOperation = "increase" | "decrease" | "set"

const DEFAULT_PAGE_SIZE = 25

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100].map((value) => ({ label: String(value), value: String(value) }))

const toRow = (product: ProductEntity): ProductQuickEditItem => ({
  id: product.id,
  name: product.catalogProductEntity.name,
  valueBase: product.catalogProductEntity.valueBase,
  status: product.catalogProductEntity.status,
  categoryId: product.catalogProductEntity.categoryId,
  hasStockControl: product.catalogProductEntity.hasStockControl,
  stockQuantity: product.catalogProductEntity.stockQuantity,
  needFulfillment: product.catalogProductEntity.needFulfillment,
  hasPreparationTime: product.catalogProductEntity.hasPreparationTime,
  preparationTimeMinutes: product.catalogProductEntity.preparationTimeMinutes,
})

const hasRowChanged = (row: ProductQuickEditItem, initialRow?: ProductQuickEditItem) =>
  !initialRow ||
  row.name !== initialRow.name ||
  row.valueBase !== initialRow.valueBase ||
  row.status !== initialRow.status ||
  row.categoryId !== initialRow.categoryId ||
  row.hasStockControl !== initialRow.hasStockControl ||
  row.stockQuantity !== initialRow.stockQuantity ||
  row.needFulfillment !== initialRow.needFulfillment ||
  row.hasPreparationTime !== initialRow.hasPreparationTime ||
  row.preparationTimeMinutes !== initialRow.preparationTimeMinutes

export const ProductQuickEditPopup = ({ products, onClose, onReload }: ProductQuickEditPopupProps) => {
  const { t } = useTranslation("quickEdit")

  const initialRows = useMemo(() => products.map(toRow), [products])

  const [rows, setRows] = useState(initialRows)

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())

  const [search, setSearch] = useState("")

  const [currentPage, setCurrentPage] = useState(1)

  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)

  const [priceOperation, setPriceOperation] = useState<PriceOperation>("increase")

  const [bulkValue, setBulkValue] = useState("10")

  const [bulkCategoryId, setBulkCategoryId] = useState("")

  const [bulkStatus, setBulkStatus] = useState("")

  const [bulkName, setBulkName] = useState("")

  const [bulkStockQuantity, setBulkStockQuantity] = useState("")

  const [bulkPreparationMinutes, setBulkPreparationMinutes] = useState("")

  const [bulkStockControl, setBulkStockControl] = useState<boolean>()

  const [bulkNeedFulfillment, setBulkNeedFulfillment] = useState<boolean>()

  const [bulkPreparationControl, setBulkPreparationControl] = useState<boolean>()

  const { handleQuickEditProducts } = useQuickEditProducts()

  const categoryOptions = useMemo(
    () =>
      categoryMemory
        .getAll()
        .filter((category) => category.name.trim().toLowerCase() !== "combos")
        .map((category) => ({ label: category.name, value: String(category.id) })),
    [],
  )
  const statusOptions = useMemo(
    () => [
      { label: t("status.active"), value: String(EEnableStatusType.Active) },
      { label: t("status.inactive"), value: String(EEnableStatusType.Inactive) },
    ],
    [t],
  )
  const updateSelected = (updater: (row: ProductQuickEditItem) => ProductQuickEditItem) => {
    if (selectedIds.size === 0) return
    setRows((current) => current.map((row) => (selectedIds.has(row.id) ? updater(row) : row)))
  }

  const applyNumericBulkValue = (
    value: string,
    updater: (row: ProductQuickEditItem, parsedValue: number) => ProductQuickEditItem,
  ) => {
    const parsedValue = Number(value)
    if (!Number.isInteger(parsedValue) || parsedValue < 0) return
    updateSelected((row) => updater(row, parsedValue))
  }

  const applyPriceOperation = () => {
    const value = Number(bulkValue)
    if (!Number.isFinite(value) || value < 0 || (priceOperation === "decrease" && value >= 100)) return

    updateSelected((row) => {
      const calculated =
        priceOperation === "set"
          ? value
          : priceOperation === "increase"
            ? row.valueBase * (1 + value / 100)
            : row.valueBase * (1 - value / 100)

      return { ...row, valueBase: Math.round(calculated * 100) / 100 }
    })
  }

  const selectedRows = rows.filter((row) => selectedIds.has(row.id))

  const hasStockEnabledSelection = selectedRows.some((row) => row.hasStockControl)

  const hasFulfillmentEnabledSelection = selectedRows.some((row) => row.needFulfillment)

  const hasPreparationEnabledSelection = selectedRows.some((row) => row.needFulfillment && row.hasPreparationTime)

  const columns: QuickEditColumn<ProductQuickEditItem>[] = [
    {
      id: "name",
      title: t("columns.name"),
      descriptionKey: "interactionDescriptions:components.quickEditGrid.productQuickEditPopup.input.name",
      type: "text",
      group: "input",
      minWidthClass: "min-w-[220px]",
      getValue: (row) => row.name,
      setValue: (row, value) => ({ ...row, name: String(value) }),
      validate: (value) => (String(value).trim() ? undefined : t("validation.requiredName")),
      bulkEditor: (
        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1">
            <InputField
              type="text"
              value={bulkName}
              placeholder={t("bulk.name")}
              descriptionKey="interactionDescriptions:components.quickEditGrid.productQuickEditPopup.input.bulkName"
              onChange={(event) => setBulkName(event.target.value)}
            />
          </div>

          <ButtonIcon
            sizeNumber={SIZE.buttonExpandIcon}
            bgColor="#f97316"
            iconColor="#ffffff"
            icon={faCheck}
            ariaLabel={t("actions.apply")}
            disabled={selectedIds.size === 0 || !bulkName.trim()}
            descriptionKey="interactionDescriptions:components.quickEditGrid.productQuickEditPopup.button.applyBulkName"
            onClick={() => {
              if (selectedIds.size > 0 && bulkName.trim()) {
                updateSelected((row) => ({ ...row, name: bulkName.trim() }))
              }
            }}
          />
        </div>
      ),
    },
    {
      id: "valueBase",
      title: t("columns.price"),
      descriptionKey: "interactionDescriptions:components.quickEditGrid.productQuickEditPopup.input.price",
      type: "money",
      group: "input",
      minWidthClass: "min-w-[140px]",
      getValue: (row) => row.valueBase,
      setValue: (row, value) => ({ ...row, valueBase: Number(value) }),
      validate: (value) => (Number(value) > 0 ? undefined : t("validation.positivePrice")),
      bulkEditor: (
        <div className="space-y-2">
          <GenericDropDown
            data={[
              { label: t("bulk.increasePercent"), value: "increase" },
              { label: t("bulk.decreasePercent"), value: "decrease" },
              { label: t("bulk.setPrice"), value: "set" },
            ]}
            initialState={priceOperation}
            disable={selectedIds.size === 0}
            descriptionKey="interactionDescriptions:components.quickEditGrid.productQuickEditPopup.select.bulkPriceOperation"
            handlerFuncion={(event) => setPriceOperation(event.target.value as PriceOperation)}
          />
          <div className="flex items-center gap-2">
            <div className="min-w-0 flex-1">
              <InputField
                type="number"
                value={bulkValue}
                placeholder={t("bulk.value")}
                disable={selectedIds.size === 0}
                min={0}
                max={priceOperation === "decrease" ? 99.99 : undefined}
                step={0.01}
                descriptionKey="interactionDescriptions:components.quickEditGrid.productQuickEditPopup.input.bulkPriceValue"
                onChange={(event) => setBulkValue(event.target.value)}
              />
            </div>

            <ButtonIcon
              sizeNumber={SIZE.buttonExpandIcon}
              bgColor="#f97316"
              iconColor="#ffffff"
              icon={faCheck}
              ariaLabel={t("actions.apply")}
              disabled={
                selectedIds.size === 0 ||
                !Number.isFinite(Number(bulkValue)) ||
                Number(bulkValue) < 0 ||
                (priceOperation === "decrease" && Number(bulkValue) >= 100)
              }
              descriptionKey="interactionDescriptions:components.quickEditGrid.productQuickEditPopup.button.applyBulkPrice"
              onClick={applyPriceOperation}
            />
          </div>
        </div>
      ),
    },
    {
      id: "categoryId",
      title: t("columns.category"),
      descriptionKey: "interactionDescriptions:components.quickEditGrid.productQuickEditPopup.select.category",
      type: "select",
      group: "select",
      minWidthClass: "min-w-[190px]",
      options: categoryOptions,
      getValue: (row) => String(row.categoryId),
      setValue: (row, value) => ({ ...row, categoryId: Number(value) }),
      validate: (value) => (Number(value) > 0 ? undefined : t("validation.requiredCategory")),
      bulkEditor: (
        <GenericDropDown
          data={[{ label: t("bulk.chooseCategory"), value: "" }, ...categoryOptions]}
          initialState={bulkCategoryId}
          disable={selectedIds.size === 0}
          descriptionKey="interactionDescriptions:components.quickEditGrid.productQuickEditPopup.select.bulkCategory"
          handlerFuncion={(event) => {
            setBulkCategoryId(event.target.value)
            if (event.target.value) updateSelected((row) => ({ ...row, categoryId: Number(event.target.value) }))
          }}
        />
      ),
    },
    {
      id: "status",
      title: t("columns.status"),
      descriptionKey: "interactionDescriptions:components.quickEditGrid.productQuickEditPopup.select.status",
      type: "select",
      group: "select",
      options: statusOptions,
      getValue: (row) => String(row.status),
      setValue: (row, value) => ({ ...row, status: Number(value) as EEnableStatusType }),
      bulkEditor: (
        <GenericDropDown
          data={[{ label: t("bulk.chooseStatus"), value: "" }, ...statusOptions]}
          initialState={bulkStatus}
          disable={selectedIds.size === 0}
          descriptionKey="interactionDescriptions:components.quickEditGrid.productQuickEditPopup.select.bulkStatus"
          handlerFuncion={(event) => {
            setBulkStatus(event.target.value)
            if (event.target.value) {
              updateSelected((row) => ({ ...row, status: Number(event.target.value) as EEnableStatusType }))
            }
          }}
        />
      ),
    },
    {
      id: "hasStockControl",
      title: t("columns.stockControl"),
      descriptionKey: "interactionDescriptions:components.quickEditGrid.productQuickEditPopup.toggle.stockControl",
      type: "boolean",
      group: "boolean",
      minWidthClass: "min-w-[180px]",
      getValue: (row) => row.hasStockControl,
      setValue: (row, value) => {
        const enabled = Boolean(value)

        return { ...row, hasStockControl: enabled, stockQuantity: enabled ? row.stockQuantity : 0 }
      },
      bulkEditor: (
        <QuickEditBooleanSelect
          value={bulkStockControl}
          trueLabel={t("bulk.yes")}
          falseLabel={t("bulk.no")}
          disabled={selectedIds.size === 0}
          descriptionKey="interactionDescriptions:components.quickEditGrid.productQuickEditPopup.toggle.bulkStockControl"
          onChange={(enabled) => {
            setBulkStockControl(enabled)
            updateSelected((row) => ({
              ...row,
              hasStockControl: enabled,
              stockQuantity: enabled ? row.stockQuantity : 0,
            }))
          }}
        />
      ),
    },
    {
      id: "stockQuantity",
      title: t("columns.stockQuantity"),
      descriptionKey: "interactionDescriptions:components.quickEditGrid.productQuickEditPopup.input.stockQuantity",
      type: "number",
      group: "input",
      minWidthClass: "min-w-[150px]",
      getValue: (row) => row.stockQuantity,
      setValue: (row, value) => ({ ...row, stockQuantity: Number(value) }),
      isDisabled: (row) => !row.hasStockControl,
      disabledMessage: t("dependencies.enableStockControl"),
      validate: (value, row) =>
        row.hasStockControl && (!Number.isInteger(Number(value)) || Number(value) < 0)
          ? t("validation.nonNegativeStock")
          : undefined,
      bulkEditor: (
        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1">
            <InputField
              type="number"
              value={bulkStockQuantity}
              placeholder={t("bulk.quantity")}
              disable={!hasStockEnabledSelection}
              min={0}
              step={1}
              descriptionKey="interactionDescriptions:components.quickEditGrid.productQuickEditPopup.input.bulkStockQuantity"
              onChange={(event) => setBulkStockQuantity(event.target.value)}
            />
          </div>

          <ButtonIcon
            sizeNumber={SIZE.buttonExpandIcon}
            bgColor="#f97316"
            iconColor="#ffffff"
            icon={faCheck}
            ariaLabel={t("actions.apply")}
            disabled={!hasStockEnabledSelection}
            descriptionKey="interactionDescriptions:components.quickEditGrid.productQuickEditPopup.button.applyBulkStockQuantity"
            onClick={() => {
              if (selectedIds.size > 0 && bulkStockQuantity) {
                applyNumericBulkValue(bulkStockQuantity, (row, value) =>
                  row.hasStockControl ? { ...row, stockQuantity: value } : row,
                )
              }
            }}
          />
        </div>
      ),
    },
    {
      id: "needFulfillment",
      title: t("columns.needFulfillment"),
      descriptionKey: "interactionDescriptions:components.quickEditGrid.productQuickEditPopup.toggle.needFulfillment",
      type: "boolean",
      group: "boolean",
      minWidthClass: "min-w-[180px]",
      getValue: (row) => row.needFulfillment,
      setValue: (row, value) => {
        const enabled = Boolean(value)

        return {
          ...row,
          needFulfillment: enabled,
          hasPreparationTime: enabled ? row.hasPreparationTime : false,
          preparationTimeMinutes: enabled ? row.preparationTimeMinutes : 0,
        }
      },
      bulkEditor: (
        <QuickEditBooleanSelect
          value={bulkNeedFulfillment}
          trueLabel={t("bulk.yes")}
          falseLabel={t("bulk.no")}
          disabled={selectedIds.size === 0}
          descriptionKey="interactionDescriptions:components.quickEditGrid.productQuickEditPopup.toggle.bulkNeedFulfillment"
          onChange={(enabled) => {
            setBulkNeedFulfillment(enabled)
            updateSelected((row) => ({
              ...row,
              needFulfillment: enabled,
              hasPreparationTime: enabled ? row.hasPreparationTime : false,
              preparationTimeMinutes: enabled ? row.preparationTimeMinutes : 0,
            }))
          }}
        />
      ),
    },
    {
      id: "hasPreparationTime",
      title: t("columns.preparationControl"),
      descriptionKey:
        "interactionDescriptions:components.quickEditGrid.productQuickEditPopup.toggle.preparationControl",
      type: "boolean",
      group: "boolean",
      minWidthClass: "min-w-[180px]",
      getValue: (row) => row.hasPreparationTime,
      isDisabled: (row) => !row.needFulfillment,
      disabledMessage: t("dependencies.enableFulfillment"),
      setValue: (row, value) => {
        const enabled = Boolean(value)

        return {
          ...row,
          hasPreparationTime: enabled,
          preparationTimeMinutes: enabled ? Math.max(1, row.preparationTimeMinutes) : 0,
        }
      },
      bulkEditor: (
        <QuickEditBooleanSelect
          value={bulkPreparationControl}
          trueLabel={t("bulk.yes")}
          falseLabel={t("bulk.no")}
          disabled={!hasFulfillmentEnabledSelection}
          descriptionKey="interactionDescriptions:components.quickEditGrid.productQuickEditPopup.toggle.bulkPreparationControl"
          onChange={(enabled) => {
            setBulkPreparationControl(enabled)
            updateSelected((row) =>
              row.needFulfillment
                ? {
                    ...row,
                    hasPreparationTime: enabled,
                    preparationTimeMinutes: enabled ? Math.max(1, row.preparationTimeMinutes) : 0,
                  }
                : row,
            )
          }}
        />
      ),
    },
    {
      id: "preparationTimeMinutes",
      title: t("columns.preparationMinutes"),
      descriptionKey: "interactionDescriptions:components.quickEditGrid.productQuickEditPopup.input.preparationMinutes",
      type: "number",
      group: "input",
      minWidthClass: "min-w-[160px]",
      getValue: (row) => row.preparationTimeMinutes,
      setValue: (row, value) => ({ ...row, preparationTimeMinutes: Number(value) }),
      isDisabled: (row) => !row.needFulfillment || !row.hasPreparationTime,
      disabledMessage: t("dependencies.enablePreparationControl"),
      validate: (value, row) =>
        row.hasPreparationTime && (!Number.isInteger(Number(value)) || Number(value) <= 0)
          ? t("validation.positivePreparation")
          : undefined,
      bulkEditor: (
        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1">
            <InputField
              type="number"
              value={bulkPreparationMinutes}
              placeholder={t("bulk.minutes")}
              disable={!hasPreparationEnabledSelection}
              min={1}
              step={1}
              descriptionKey="interactionDescriptions:components.quickEditGrid.productQuickEditPopup.input.bulkPreparationMinutes"
              onChange={(event) => setBulkPreparationMinutes(event.target.value)}
            />
          </div>
          <ButtonIcon
            sizeNumber={SIZE.buttonExpandIcon}
            bgColor="#f97316"
            iconColor="#ffffff"
            icon={faCheck}
            ariaLabel={t("actions.apply")}
            disabled={!hasPreparationEnabledSelection}
            descriptionKey="interactionDescriptions:components.quickEditGrid.productQuickEditPopup.button.applyBulkPreparationMinutes"
            onClick={() => {
              if (selectedIds.size > 0 && bulkPreparationMinutes) {
                applyNumericBulkValue(bulkPreparationMinutes, (row, value) =>
                  row.needFulfillment && row.hasPreparationTime ? { ...row, preparationTimeMinutes: value } : row,
                )
              }
            }}
          />
        </div>
      ),
    },
  ]

  const initialRowsById = useMemo(() => new Map(initialRows.map((row) => [row.id, row])), [initialRows])

  const changedRows = useMemo(
    () => rows.filter((row) => hasRowChanged(row, initialRowsById.get(row.id))),
    [initialRowsById, rows],
  )

  const filteredRows = useMemo(() => {
    const normalizedSearch = search.trim().toLocaleLowerCase()

    return rows.filter((row) => row.name.toLocaleLowerCase().includes(normalizedSearch))
  }, [rows, search])

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize))

  const effectiveCurrentPage = Math.min(currentPage, totalPages)

  const firstItemIndex = (effectiveCurrentPage - 1) * pageSize

  const paginatedRows = useMemo(
    () => filteredRows.slice(firstItemIndex, firstItemIndex + pageSize),
    [filteredRows, firstItemIndex, pageSize],
  )

  const firstVisibleItem = filteredRows.length === 0 ? 0 : firstItemIndex + 1

  const lastVisibleItem = Math.min(firstItemIndex + pageSize, filteredRows.length)

  const hasValidationErrors = rows.some((row) => columns.some((column) => column.validate?.(column.getValue(row), row)))

  const save = async () => {
    if (changedRows.length === 0 || hasValidationErrors) return
    const saved = await handleQuickEditProducts(changedRows, onReload)
    if (saved) onClose()
  }

  const preventCloseWithChanges = () => {
    if (changedRows.length > 0 && !window.confirm(t("confirmDiscard"))) return
    onClose()
  }

  return (
    <ModalPortal>
      <div className="fixed inset-0 z-[960] flex items-center justify-center bg-black/95 p-2">
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className="flex h-[calc(100dvh-1rem-env(safe-area-inset-top,0px)-env(safe-area-inset-bottom,0px))] w-[calc(100vw-1rem-env(safe-area-inset-left,0px)-env(safe-area-inset-right,0px))] max-w-none flex-col items-center justify-center overflow-hidden rounded-xl bg-white p-1 shadow-lg">
            <TitlePageResponsiveIconSmall
              title={t("title")}
              icon={faTableCells}
              iconColor={THEME.colors.iconBGSecondaryColor}
              children={
                <div className="flex items-center justify-center gap-2">
                  <PopupHelpButton
                    helpKey="components.productQuickEditPopup.1"
                    variant="global"
                    title={t("helpPopup:global.productQuickEdit.title")}
                    description={t("helpPopup:global.productQuickEdit.description")}
                    bullets={t("helpPopup:global.productQuickEdit.bullets", { returnObjects: true }) as string[]}
                  />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={preventCloseWithChanges}
                    size="small"
                    descriptionKey="interactionDescriptions:components.quickEditGrid.productQuickEditPopup.button.close"
                  />
                </div>
              }
            />

            <div className="flex min-h-0 w-full flex-1 flex-col px-3 pb-3">
              <Animate type={AnimationTypes.fadeInTraslate} duration="0.35s" delay="0.08s">
                <div className="my-3 flex flex-wrap items-center gap-3">
                  <div className="min-w-[240px] flex-1">
                    <InputField
                      type="text"
                      value={search}
                      placeholder={t("search")}
                      descriptionKey="interactionDescriptions:components.quickEditGrid.productQuickEditPopup.input.search"
                      onChange={(event) => {
                        setSearch(event.target.value)
                        setCurrentPage(1)
                      }}
                    />
                  </div>
                  <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                    <span>{t("summary.visible", { count: filteredRows.length })}</span>
                    <span>{t("summary.selected", { count: selectedIds.size })}</span>
                    <span className={changedRows.length ? "font-semibold text-orange-700" : undefined}>
                      {t("summary.changed", { count: changedRows.length })}
                    </span>
                  </div>
                </div>
              </Animate>

              <QuickEditGrid
                key={`${effectiveCurrentPage}-${pageSize}-${search}`}
                rows={paginatedRows}
                columns={columns}
                selectedIds={selectedIds}
                getRowId={(row) => row.id}
                onRowsChange={(visibleRows) => {
                  const byId = new Map(visibleRows.map((row) => [row.id, row]))
                  setRows((current) => current.map((row) => byId.get(row.id) ?? row))
                }}
                onSelectionChange={setSelectedIds}
                selectAllLabel={t("selectAll")}
                bulkSelectionLabel={t("bulk.selectedOnly")}
                emptyMessage={t("empty")}
                booleanTrueLabel={t("bulk.yes")}
                booleanFalseLabel={t("bulk.no")}
                selectAllDescriptionKey="interactionDescriptions:components.quickEditGrid.quickEditGrid.input.selectAll"
                selectRowDescriptionKey="interactionDescriptions:components.quickEditGrid.quickEditGrid.input.selectRow"
              />

              {filteredRows.length > 0 && (
                <Animate type={AnimationTypes.fadeInTraslate} duration="0.35s" delay="0.12s">
                  <div className="mt-3 flex flex-none flex-wrap items-center justify-center gap-x-4 gap-y-3 rounded-xl border border-orange-200 bg-orange-50/60 px-3 py-2.5 text-sm text-gray-600 md:justify-between">
                    <div className="flex items-center gap-2 whitespace-nowrap font-semibold">
                      <span>{t("pagination.itemsPerPage")}</span>

                      <div className="w-[5.25rem]">
                        <GenericDropDown
                          name="quick-edit-page-size"
                          data={PAGE_SIZE_OPTIONS}
                          initialState={String(pageSize)}
                          descriptionKey="interactionDescriptions:components.quickEditGrid.productQuickEditPopup.select.pageSize"
                          handlerFuncion={(event) => {
                            const nextPageSize = Number(event.target.value)

                            if (!PAGE_SIZE_OPTIONS.some((option) => Number(option.value) === nextPageSize)) return

                            setPageSize(nextPageSize)
                            setCurrentPage(1)
                          }}
                        />
                      </div>
                    </div>

                    <span
                      className="order-3 w-full text-center font-semibold text-gray-500 md:order-none md:w-auto"
                      aria-live="polite"
                    >
                      {t("pagination.showing", {
                        start: firstVisibleItem,
                        end: lastVisibleItem,
                        total: filteredRows.length,
                      })}
                    </span>

                    <nav className="flex items-center gap-2.5" aria-label={t("pagination.ariaLabel")}>
                      <button
                        type="button"
                        className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg bg-orange-500 px-3 py-2 font-bold text-white transition hover:-translate-y-px hover:bg-orange-600 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:translate-y-0 disabled:hover:shadow-none"
                        disabled={effectiveCurrentPage === 1}
                        onClick={() => setCurrentPage(Math.max(effectiveCurrentPage - 1, 1))}
                        aria-label={t("pagination.previous")}
                        data-interaction-description-key="interactionDescriptions:components.quickEditGrid.productQuickEditPopup.button.previousPage"
                      >
                        <FontAwesomeIcon icon={faChevronLeft} />
                        <span className="hidden sm:inline">{t("pagination.previous")}</span>
                      </button>

                      <div
                        className="flex min-w-[7.5rem] items-center justify-center gap-1 whitespace-nowrap"
                        aria-live="polite"
                      >
                        <span>{t("pagination.page")}</span>
                        <strong className="text-lg text-orange-500">{effectiveCurrentPage}</strong>
                        <span>{t("pagination.of", { totalPages })}</span>
                      </div>

                      <button
                        type="button"
                        className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg bg-orange-500 px-3 py-2 font-bold text-white transition hover:-translate-y-px hover:bg-orange-600 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:translate-y-0 disabled:hover:shadow-none"
                        disabled={effectiveCurrentPage === totalPages}
                        onClick={() => setCurrentPage(Math.min(effectiveCurrentPage + 1, totalPages))}
                        aria-label={t("pagination.next")}
                        data-interaction-description-key="interactionDescriptions:components.quickEditGrid.productQuickEditPopup.button.nextPage"
                      >
                        <span className="hidden sm:inline">{t("pagination.next")}</span>
                        <FontAwesomeIcon icon={faChevronRight} />
                      </button>
                    </nav>
                  </div>
                </Animate>
              )}

              <Animate type={AnimationTypes.fadeInTraslate} duration="0.35s" delay="0.16s">
                <div className="mt-3 flex flex-wrap justify-end gap-2 border-t border-gray-200 pt-3">
                  <Button
                    label={t("actions.cancel")}
                    type="secondary"
                    descriptionKey="interactionDescriptions:components.quickEditGrid.productQuickEditPopup.button.cancel"
                    onClick={preventCloseWithChanges}
                  />

                  <Button
                    label={t("actions.save", { count: changedRows.length })}
                    disabled={changedRows.length === 0 || hasValidationErrors}
                    descriptionKey="interactionDescriptions:components.quickEditGrid.productQuickEditPopup.button.save"
                    onClick={save}
                  />
                </div>
              </Animate>
            </div>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
